import React, { FC, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import classNames from "classnames";
import { formatDate, IComment, toMmSs } from "./constants";
import { CommentMenu } from "./CommentMenu";
import { AppliedVideosChip } from "./AppliedVideosChip";
import { useUser } from "../../../../hooks/useUser";
import { useVideoFeedback } from "./useVideoFeedback";
import { DotLottleLoader } from "../../../../components/Loader";
import { isInternal } from "../../../../utils";
import { IconCheck, IconCross } from "../../../../utils/icons";
import {
    MentionText,
    TaggableUser,
    existingMentions,
    serializeMentions,
    toDraft,
} from "../../../../components/Mentions/MentionText";
import { MentionTextarea } from "../../../../components/Mentions/MentionTextarea";
import { commentTaggableUsers } from "../../../../api/endpoints";

interface Props {
    commentList: { date: string; commentsArr: IComment[] };
    focusCommentId?: string | null;
}

export const CommentList: FC<Props> = ({ commentList, focusCommentId }) => {
    const { user, role } = useUser();
    const { onUpdateComment, isReviewLocked, isPublicPage, onCopyComment, setCommentToApply, readOnly, project } = useVideoFeedback();
    const isInternalUser = isInternal(role);
    const { date, commentsArr } = commentList;
    const [editableComment, setEditableComment] = useState<IComment>();
    const [picked, setPicked] = useState<TaggableUser[]>([]);
    const [loadingComment, setLoadingComment] = useState<string>();

    const getGreyText = (text: string) => (
        <Typography variant="body2" className="!text-xs text-text-2">
            {text}
        </Typography>
    );

    const getDisplayName = (commentUser: any) => {
        if (commentUser?.displayName) return commentUser.displayName;
        if (commentUser?.firstName) return commentUser.firstName;
        if (commentUser?.email) return commentUser.email.split("@")[0];
        return "";
    };

    const submitEditedComment = async (comment: IComment) => {
        setLoadingComment(comment.id);
        await onUpdateComment({
            ...comment,
            ...editableComment,
            message: serializeMentions(editableComment?.message ?? comment.message, [
                ...existingMentions(comment.message),
                ...picked,
            ]),
            edited: true,
        });
        setEditableComment(undefined);
        setPicked([]);
        setLoadingComment(undefined);
    };

    const filteredComments = commentsArr.filter((comment) => isInternal(user?.role || "") || !comment.deleted);

    if (!filteredComments?.length) return null;

    return (
        <Box className="flex flex-col w-full p-4 gap-2">
            <Box className="p-2 flex items-center justify-center">{getGreyText(formatDate(date, false))}</Box>
            {filteredComments.map((comment, index) => {
                let isMyComment = false;
                if (comment.user?.userid) {
                    isMyComment = comment.user?.userid === user?.userid;
                }

                // Edit/Delete: own comment while the review is open (the API
                // only lets authors edit). Staff may also delete the client's
                // comments — a brand user's or a share-link guest's.
                const canEditDelete = Boolean(isMyComment) && !isReviewLocked;
                const canDelete =
                    canEditDelete ||
                    (isInternalUser && Boolean(comment.client_comment) && !readOnly && !isPublicPage && !comment.deleted);
                // Apply to other videos: staff can apply anyone's comment (the
                // client's feedback); brand users only their own. Copies keep
                // the original author. Staff keep it in Editing (the API
                // exempts them from the lock), but never on a view-only version.
                const canApply =
                    Boolean(onCopyComment) &&
                    !isPublicPage &&
                    !readOnly &&
                    !comment.deleted &&
                    comment.id != null &&
                    (isInternalUser || Boolean(isMyComment)) &&
                    (isInternalUser || !isReviewLocked);

                const isEditable = editableComment?.id === comment.id;
                const isFocused =
                    focusCommentId != null &&
                    String(comment.id) === String(focusCommentId);
                return (
                    <Box
                        key={index}
                        id={`review-comment-${comment.id}`}
                        className={classNames("w-full flex", {
                            "justify-end": isMyComment
                        })}
                    >
                        <Box
                            className={classNames(
                                "border rounded-2xl flex flex-col gap-4 p-4 pb-5 relative bg-surface w-full transition-shadow duration-500",
                                isFocused
                                    ? "border-accent ring-2 ring-accent"
                                    : "border-border-token"
                            )}
                        >
                            <Box className="flex flex-col w-full gap-2">
                                <Box className="flex items-start gap-2 justify-between">
                                    <Typography variant="body1">
                                        {getDisplayName(comment.user)}
                                        {/* eslint-disable-next-line no-nested-ternary */}
                                        {isInternal(user?.role || "") && comment.deleted ? (
                                            <span className="text-xs text-red-400 ml-1">Deleted</span>
                                        ) : comment.edited ? (
                                            <span className="text-xs text-muted ml-1">Edited</span>
                                        ) : null}
                                    </Typography>

                                    <Box className="flex items-center gap-1">
                                        {typeof comment.timestamp_end === "number" && (
                                            <span className="text-sm text-accent-text font-medium w-[90px]">
                                                {`${toMmSs(comment.timestamp || 0)}-${toMmSs(
                                                    comment.timestamp_end || 0
                                                )}`}
                                            </span>
                                        )}
                                        {(canEditDelete || canDelete || canApply) && (
                                            <CommentMenu
                                                comment={comment}
                                                canEdit={canEditDelete}
                                                canDelete={canDelete}
                                                onEdit={() => {
                                                    // Picks from an abandoned edit must not turn a
                                                    // plain @Name in this one into a mention.
                                                    setPicked([]);
                                                    setEditableComment({ ...comment, message: toDraft(comment.message) });
                                                }}
                                                onApplyToVideos={canApply ? () => setCommentToApply(comment) : undefined}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <Box className="flex gap-2 items-center">
                                    {isEditable ? (
                                        <>
                                            <MentionTextarea
                                                value={editableComment?.message || ""}
                                                className="border border-border-token rounded-md p-2 w-full text-xs py-1 resize-none"
                                                usersUrl={
                                                    project?.id && !isPublicPage
                                                        ? commentTaggableUsers(project.id)
                                                        : null
                                                }
                                                onMentionPicked={(taggedUser) =>
                                                    setPicked((prev) =>
                                                        prev.some((p) => p.userid === taggedUser.userid)
                                                            ? prev
                                                            : [...prev, taggedUser]
                                                    )
                                                }
                                                onChange={(message) =>
                                                    setEditableComment({ ...comment, message })
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === "Escape") submitEditedComment(comment);
                                                }}
                                                minRows={1}
                                            />
                                            <Box className="flex gap-1 items-center">
                                                <IconButton
                                                    className="!p-0 !bg-surface !border !border-text-1 !rounded-2xl w-[28px] h-[28px] items-center justify-center"
                                                    sx={{ border: 1, color: "var(--text)" }}
                                                    onClick={() => {
                                                        setPicked([]);
                                                        setEditableComment(undefined);
                                                    }}
                                                >
                                                    {/* currentColor: an SVG stroke attribute can't read var() */}
                                                    <IconCross width={20} height={20} stroke="currentColor" />
                                                </IconButton>
                                                <IconButton
                                                    className="!p-0 !bg-accent !rounded-2xl w-[28px] h-[28px]"
                                                    onClick={() => submitEditedComment(comment)}
                                                >
                                                    <IconCheck width={24} height={24} stroke="#fff" />
                                                </IconButton>
                                            </Box>
                                        </>
                                    ) : (
                                        <div className="text-text-2 text-sm w-full">
                                            <Typography
                                                variant="body2"
                                                className="!text-xs text-text-2 whitespace-pre-wrap break-words"
                                            >
                                                <MentionText text={comment.message} />
                                            </Typography>
                                        </div>
                                    )}
                                </Box>
                            </Box>
                            <Box className="flex items-center justify-between gap-2 flex-wrap">
                                {getGreyText(formatDate(comment.created_at))}
                                {(comment.applied_to?.length || 0) >= 2 && <AppliedVideosChip comment={comment} />}
                            </Box>
                            {isMyComment && loadingComment === comment.id && (
                                <Box
                                    className="absolute inset-0 flex items-center justify-center"
                                    sx={{ backgroundColor: "rgba(255,255,255,0.5)", '[data-theme="dark"] &': { backgroundColor: "rgba(24,33,47,0.5)" } }}
                                >
                                    <DotLottleLoader sx={{ width: 60, height: 60 }} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
